import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Briefcase, Contact, MapPin, Phone, Mail, Calendar } from 'lucide-react';

export const ReferenceCard = ({ reference, compact = false }) => {
  const navigate = useNavigate();

  if (!reference) return null;

  const handleClick = () => {
    if (reference.type === 'job') {
      navigate(`/operations-hub/production?job=${reference.id}`);
    } else if (reference.type === 'contact') {
      navigate(`/sales-hub/leads?contact=${reference.id}`);
    }
  };

  if (compact) {
    // Compact inline badge version (for note cards list view)
    const Icon = reference.type === 'job' ? Briefcase : Contact;
    return (
      <Badge
        variant="outline"
        className="cursor-pointer hover:bg-accent transition-colors inline-flex items-center gap-1 my-0.5"
        onClick={(e) => {
          e.stopPropagation();
          handleClick();
        }}
      >
        <Icon className="h-3 w-3" />
        {reference.type === 'job' ? '#J' : '#C'}{reference.name}
      </Badge>
    );
  }

  // Full card version (for note detail view)
  if (reference.type === 'job') {
    return (
      <Card 
        className="cursor-pointer hover:shadow-md transition-shadow bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 my-2"
        onClick={handleClick}
      >
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-600 rounded-lg text-white">
              <Briefcase className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="default" className="bg-blue-600">Job</Badge>
                <span className="font-semibold truncate">{reference.name}</span>
              </div>
              {reference.subtitle && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  <span className="truncate">{reference.subtitle}</span>
                </div>
              )}
              {reference.status && (
                <Badge variant="outline" className="mt-1 text-xs">
                  {reference.status}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (reference.type === 'contact') {
    return (
      <Card 
        className="cursor-pointer hover:shadow-md transition-shadow bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 my-2"
        onClick={handleClick}
      >
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-600 rounded-lg text-white">
              <Contact className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="default" className="bg-green-600">Contact</Badge>
                <span className="font-semibold truncate">{reference.name}</span>
              </div>
              {reference.subtitle && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  {reference.subtitle.includes('@') ? (
                    <>
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{reference.subtitle}</span>
                    </>
                  ) : (
                    <>
                      <Phone className="h-3 w-3" />
                      <span className="truncate">{reference.subtitle}</span>
                    </>
                  )}
                </div>
              )}
              {reference.status && (
                <Badge variant="outline" className="mt-1 text-xs">
                  {reference.status}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};
